import { isNullish, toArray } from "kanopi-components/utils/primitives";
import { APP_CONSTANTS } from "./appConstants";

export enum valueTypes {
  Boolean = "boolean",
  Currency = "currency",
  Date = "date",
  Number = "number",
  String = "string",
  Radio = "radio",
  Percent = "percent",
}
/**
 * Format values based on given type
 * @todo  Extend formatter to respect locales.
 *        https://app.clickup.com/t/1t20gf9
 * @returns {
    format,
    formatBoolean,
    formatCurrency,
    formatDate,
    formatNumber,
    formatRadioValue,
    formatString,
    formatPercent
  }
 */
export const useFormatter = () => {
  const typeFormatMap = {
    string: formatString,
    boolean: formatBoolean,
    currency: formatCurrency,
    date: formatDate,
    radio: formatRadioValue,
    percent: formatPercent,
  };
  // Generic formatting method, useful even if type of value unknown
  const format = (
    value: string | boolean | number | Date | undefined | null,
    type?: valueTypes,
    emptyDisplay?: string,
    options?: TRadioOptions[]
  ) => {
    const formatMethod = typeFormatMap[type] || formatString;
    let formattedValue: string;
    if (type === valueTypes.Radio) {
      formattedValue = formatMethod(value, options, emptyDisplay);
    } else if (type == valueTypes.String) {
      formattedValue = formatMethod(value, emptyDisplay);
    } else {
      formattedValue = formatMethod(value, emptyDisplay);
    }
    return formattedValue;
  };
  // Return individual formatters for convenience of caller.
  return {
    format,
    formatBoolean,
    formatCurrency,
    formatDate,
    formatDateTime,
    formatNumber,
    formatRadioValue,
    formatString,
  };
};

const formatString = (
  value: unknown,
  emptyDisplay: string = APP_CONSTANTS.EMPTY_DISPLAY
) => {
  return isNullish(value) ? emptyDisplay : value?.toString() || emptyDisplay;
};

const formatBoolean = (
  value: boolean | undefined | null,
  emptyDisplay: string = APP_CONSTANTS.EMPTY_DISPLAY
): string => {
  return isNullish(value) ? emptyDisplay : value ? "Yes" : "No";
};

const formatCurrency = (
  value: string | number | undefined | null,
  emptyDisplay: string = APP_CONSTANTS.EMPTY_DISPLAY
) => {
  const locale = undefined; // Use the host default language.
  const options = { maximumFractionDigits: 2 };

  value = typeof value === "string" ? parseFloat(value) : value;
  // TODO: Should we default to 0.0 always?
  if (isNaN(value)) {
    value = 0.0;
  }

  if (!Number.isInteger(value)) {
    options["minimumFractionDigits"] = 2;
  }
  const formattedValue = value?.toLocaleString(locale, options);

  if (formattedValue) {
    if (value >= 0) {
      return `$${formattedValue}`;
    } else {
      return `-$${formattedValue.slice(1)}`;
    }
  }
  return emptyDisplay;
};

const formatDate = (
  value: Date | string | undefined | null,
  emptyDisplay: string = APP_CONSTANTS.EMPTY_DISPLAY
) => {
  return isNullish(value) ? emptyDisplay : new Date(value).toLocaleDateString();
};

const formatDateTime = (
  value: Date | string | undefined | null,
  emptyDisplay: string = APP_CONSTANTS.EMPTY_DISPLAY
) => {
  return isNullish(value)
    ? emptyDisplay
    : new Date(value).toLocaleString(Intl.Locale.name, { hour12: true });
};

const formatNumber = (
  value: string | number | undefined | null,
  emptyDisplay: string = APP_CONSTANTS.EMPTY_DISPLAY
) => {
  return isNullish(value) ? emptyDisplay : value;
};

const formatRadioValue = (
  value: string | string[],
  options: TRadioOptions[] = [],
  emptyDisplay: string = APP_CONSTANTS.EMPTY_DISPLAY
) => {
  const mappedAnswers =
    value &&
    toArray(value).map((a: string) => options.find((o) => o?.value == a)?.title);
  const titles = mappedAnswers ? mappedAnswers.join(", ") : null;
  return titles || emptyDisplay;
};

const formatPercent = (
  value: string | number | undefined | null,
  emptyDisplay: string = APP_CONSTANTS.EMPTY_DISPLAY
) => {
  return isNullish(value) ? emptyDisplay : `${value}%`;
};
